import styled from "@emotion/styled"
import { Box, Button, IconButton, Tab, Tabs } from "@mui/material"
import { useTranslations } from "next-intl"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { CircleXIcon, NewText, Phone2Icon, useIsDesktop, XIcon } from "ui"
import { Apartment, contactButtonClick, useOutsideClick } from "utils"
import { choosenProject, submenuClick } from "utils/gtm"
import { ProjectsMessages } from "../../Projects"
import AccordionInnerDetails from "./../AccordionInnerDetails"
import { a11yProps } from "./../Apartments"
import DrawerContactForm from "./DrawerContactForm"
import DrawerGallery from "./DrawerGallery"
import { FormData } from "./PaymentForm"

const Container = styled.div`
  * {
    font-family: inherit !important;
  }
  --text-black: #011727;
  padding-top: 64px;
  padding-bottom: 72px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  position: relative;
  flex: 1;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 80px;
  }
`

const StyledInner = styled.div`
  gap: 24px;
  padding: 16px 0 24px;
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;
  flex: 1;
  height: 1px;
  background-color: #fff;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px 33px 20px;
  }
`
const TabsContainer = styled(Tabs)`
  width: 100%;
  overflow: visible;
  .MuiTabs-flexContainer {
    > * {
      flex-shrink: 0;
    }
    border-radius: 12px;
    padding: 8px;
    justify-content: space-between;
    min-width: calc(100% - 40px);
    width: max-content;
    background-color: #f7f7f7;
    display: flex;
    margin-inline: 20px;
    ${(props) => props.theme.breakpoints.up("md")} {
      margin-inline: 0;
      min-width: 100%;
    }
  }
  .MuiTabs-scroller {
    overflow: auto !important;
  }
`

const TabsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
`

const StyledTab = styled(Tab)<{ active: boolean }>`
  font-family: var(--rag) !important;
  * {
    font-family: inherit !important;
  }
  flex-grow: 1;
  min-height: 0;
  padding: ${({ active }) => (active ? "8px" : "8px 0")};
  min-width: 0;
  border-radius: 8px;
  font-size: 20px;
  font-weight: 700;
  line-height: 120%;
  color: ${({ active }) => (active ? "#fff !important" : "#1F375D !important")};
  background-color: ${({ active, theme }) =>
    active ? theme.colors.buttonActiveBackgroundColor : "transparent"};
`

const StyledBtnsWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  background-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  width: 100%;
  gap: 10px;
  flex-shrink: 0;
  height: 72px;
  padding: 16px 20px;
  z-index: 3;
  > * {
    flex: 1;
    width: 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 16px;
    height: 80px;
    gap: 16px;
  }
`

const StyledButton = styled(Button)`
  border-radius: 36px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 24px;
  justify-content: center;
`

const WhiteStyledButton = styled(StyledButton)`
  align-items: center;
  border: 1px solid ${(props) => props.theme.colors.darkBlue4rentMarketing};
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  background-color: white;
  gap: 4px;

  svg {
    color: inherit;
    flex-shrink: 0;
    stroke: currentColor;
  }

  &:hover {
    border-color: white;
    color: white;

    background-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  }
`

const BlueStyledButton = styled(StyledButton)`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);

  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

const IWantRentBtn = styled(BlueStyledButton)`
  display: flex;
`
const ScrollableActiveWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.up("md")} {
    overflow: visible;
    flex: unset;
  }
`

const StyledActive = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding-inline: 20px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  height: 100%;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-inline: 0;
    overflow: visible;
    height: auto;
  }
`

const CustomScrollbar = styled.div<{ $thumbHeight: number; $thumbTop: number }>`
  position: absolute;
  left: 4px;
  top: 0;
  bottom: 0;
  width: 4px;
  pointer-events: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
  &::after {
    content: "";
    position: absolute;
    top: ${(props) => props.$thumbTop}%;
    width: 4px;
    height: ${(props) => props.$thumbHeight}%;
    background: #b0b8c1;
    border-radius: 200px;
    will-change: top;
  }
`

const HiddenButtons = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  width: 100%;
  margin-bottom: 8px;
`

const MobileOptionButton = styled(Button)<{ component?: React.ElementType }>`
  width: 100%;
  border-radius: 36px;
  min-height: 48px;
  font-size: 20px;
  font-weight: 700;
  background-color: white;
  border: 2px solid ${(props) => props.theme.colors.darkBlue4rentMarketing};
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  &:hover {
    background-color: #f7f7f7;
    color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  }
`
const StyledPhoneLink = styled.a`
  width: 100%;
  text-decoration: none;
`

const DrawerHeader = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  height: 64px;
  width: 100%;
  padding: 16px 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
    padding: 16px 33px;
  }
`
const DrawerHeaderTexts = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  gap: 20px;
  width: 100%;
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  gap: 8px;
`

const RentRangePriceText = styled.div`
  flex-shrink: 0;
  white-space: nowrap;
  margin: 0;
  font-size: 18px;
  font-weight: 250;
  line-height: 150%;
  letter-spacing: -0.36px;
  order: -1;
  color: white;
  display: flex;
  align-items: center;
  gap: 4px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
    font-weight: 400;
    line-height: normal;
    flex: 1;
    text-align: end;
    order: unset;
    color: var(--text-black);
  }
`

const RentRangePriceStrong = styled.strong`
  margin: 0;
  font-weight: 400;
  line-height: 150%;
  font-size: 30px;
  color: inherit;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 32px;
    line-height: 36px;
    flex: 1;
  }
`

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.up("md")} {
    overflow: auto;
    height: 1px;
  }
`

const PaddingBottom = styled.div<{ padding: number }>`
  padding-bottom: ${(props) => props.padding || 0}px;
`

type TabProps = {
  value: number
  index: number
  apartment: Apartment
  projectPage?: boolean
  imageGallery?: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }
  buildingCharacteristicsText?: string
}
export type TabNameType = "plan" | "specs" | "docs" | "gallery" | "rentplan"
export type NewTabNameType =
  | "buildingFeatures"
  | "apartmentFeatures"
  | "projectLocation"
  | "apartmentPlan"
  | "documents"
  | "apartmentInfo"
  | "projectInfo"

function ActiveTab({ index, value, apartment }: TabProps) {
  const getNewType = (): NewTabNameType => {
    switch (index) {
      case 0:
        return "apartmentInfo"
      case 1:
        return "projectInfo"
      default:
        return "apartmentInfo"
    }
  }
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      <PaddingBottom padding={40}>
        {value === index && (
          <AccordionInnerDetails apartment={apartment} type={getNewType()} forDrawer />
        )}
      </PaddingBottom>
    </div>
  )
}

type Props = {
  apartment: Apartment
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  bmbyId?: number
  status?: any
  projectPage?: boolean
  imageGallery?: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }
  companyId?: number
  // eslint-disable-next-line no-unused-vars
  handleDrawerClose: (_event: React.MouseEvent<HTMLButtonElement>) => void
  buildingCharacteristicsText?: string
  locale: string
  bmbyMeetingId?: string | number
  fontFamilyVariable?: string
  phone?: string
  rentProjectInterestedNum?: number
  rentalProjects?: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
  preSelectedProject?: string
  leaveDetailsButtonText?: string
}

function DrawerInner({
  apartment,
  formSubjects,
  messages,
  status,
  projectPage,
  imageGallery,
  companyId,
  handleDrawerClose,
  locale,
  fontFamilyVariable,
  phone: rawPhone,
  rentProjectInterestedNum,
  rentalProjects,
  cmsProjectLeadIds,
  preSelectedProject,
  leaveDetailsButtonText,
}: Props) {
  const phone = rawPhone?.replace(/^tel:/i, "")
  const isDesktop = useIsDesktop()
  const t = useTranslations("AccordionDetails")
  const [value, setValue] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [mobileCtaOpen, setMobileCtaOpen] = useState(false)
  const [scrollThumb, setScrollThumb] = useState({ height: 0, top: 0 })
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight <= clientHeight) {
      setScrollThumb({ height: 0, top: 0 })
      return
    }
    const thumbH = (clientHeight / scrollHeight) * 100
    const thumbT = (scrollTop / (scrollHeight - clientHeight)) * (100 - thumbH)
    setScrollThumb({ height: thumbH, top: thumbT })
  }, [])

  const formRef = useRef(null)

  const handleFormClose = () => {
    setDrawerOpen(false)
  }
  const handleFormOpen = () => {
    choosenProject("מעוניינ/ת בדירה", apartment?.propertyTitle)
    submenuClick("מעוניינ/ת בדירה", apartment?.propertyTitle)
    setDrawerOpen(true)
  }

  useEffect(() => {
    handleScroll()
  }, [value, handleScroll])

  useOutsideClick([formRef], handleFormClose)
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    submenuClick(
      newValue === 0 ? "תוכנית דירה" : newValue === 1 ? "מפרט דירה" : "מסמכים וחוזים",
      apartment?.propertyTitle,
    )
    setValue(newValue)
  }
  const formData: FormData = {
    price: apartment.price,
    currency: apartment.priceCurrency,
    area: apartment.areaInSquareMeters,
    floor: apartment.floor,
    rooms: apartment.numberOfRooms,
    assetName: apartment.assetName,
    buildingName: apartment.buildingTitle,
    apartmentNumber: apartment.apartmentNumber,
  }
  const galleryImages = imageGallery?.apartmentGallery?.length
    ? imageGallery.apartmentGallery
    : apartment.gallery?.length
    ? apartment.gallery.filter((img): img is string => typeof img === "string")
    : []

  const isTheApartmentTaken = apartment.moreInfoContentHTML === "משוריין"
  return (
    <Container className={fontFamilyVariable && fontFamilyVariable}>
      {/* header */}
      <DrawerHeader>
        <DrawerHeaderTexts>
          <NewText
            component="p"
            desktopFontSize={24}
            desktopLineHeight={"28px"}
            mobileFontSize={20}
            desktopFontWeight={700}
            color="inherit"
            sx={{
              margin: 0,
              padding: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {apartment.propertyTitle} {apartment?.numberOfRooms} {t("numberOfRooms")}
          </NewText>
          <NewText
            component="span"
            desktopFontSize={24}
            mobileFontSize={20}
            desktopFontWeight={700}
            mobileFontWeight={700}
            color="inherit"
            sx={{ margin: 0, padding: 0 }}
          >
            |
          </NewText>
          <NewText
            component="p"
            desktopFontSize={24}
            desktopLineHeight={"28px"}
            mobileFontSize={20}
            desktopFontWeight={700}
            color="inherit"
            sx={{
              margin: 0,
              padding: 0,
              whiteSpace: "nowrap",
            }}
          >
            {t("apartmentNo", { num: apartment?.apartmentNumber })}
          </NewText>
          {isDesktop && (
            <RentRangePriceText>
              <RentRangePriceStrong>
                {apartment?.priceCurrency ?? "₪"}
                {typeof apartment?.price === "number"
                  ? apartment.price.toLocaleString()
                  : apartment?.price ?? "__"}
              </RentRangePriceStrong>
              {t("monthlyRent")}
            </RentRangePriceText>
          )}
        </DrawerHeaderTexts>
        <IconButton
          sx={{
            flexShrink: 0,
            marginInlineStart: "10px",
            borderRadius: "50%",
            border: "1px solid #F7F7F7",
            background: "rgba(255, 255, 255, 0.50)",
            padding: "5px",
          }}
          onClick={handleDrawerClose}
        >
          <XIcon width={20} height={20} />
        </IconButton>
      </DrawerHeader>
      {/* scrollable content - tabs */}
      <ScrollableContent>
        {galleryImages.length > 0 && (
          <DrawerGallery
            locale={locale}
            images={galleryImages}
            expandIcon
            rentProjectInterestedNum={rentProjectInterestedNum}
            galleryContainerStyles={{
              paddingTop: 0,
            }}
          />
        )}
        <StyledInner>
          <TabsContent>
            <TabsContainer value={value} onChange={handleChange} aria-label="property types">
              <StyledTab
                active={0 === value}
                key={0}
                label={t("apartmentInfo")}
                {...a11yProps(0)}
              />
              <StyledTab active={1 === value} key={1} label={t("projectInfo")} {...a11yProps(1)} />
            </TabsContainer>
          </TabsContent>
          <ScrollableActiveWrapper>
            {scrollThumb.height > 0 && scrollThumb.height < 100 && (
              <CustomScrollbar $thumbHeight={scrollThumb.height} $thumbTop={scrollThumb.top} />
            )}
            <StyledActive ref={scrollRef} onScroll={handleScroll}>
              {Array.from([0, 1]).map((el, i) => (
                <ActiveTab
                  key={i}
                  index={i}
                  value={value}
                  apartment={apartment}
                  projectPage={projectPage}
                  imageGallery={imageGallery}
                />
              ))}
            </StyledActive>
          </ScrollableActiveWrapper>
        </StyledInner>
      </ScrollableContent>
      {/* buttons */}
      {isDesktop ? (
        <StyledBtnsWrapper>
          {!isTheApartmentTaken && (
            <IWantRentBtn variant="outlined" onClick={() => handleFormOpen()}>
              {leaveDetailsButtonText || apartment.leaveDetailsButton || t("leaveDetails")}
            </IWantRentBtn>
          )}
          {phone && (
            <WhiteStyledButton
              variant="outlined"
              disableRipple
              sx={{ cursor: "default", "&:hover": { backgroundColor: "transparent" } }}
            >
              <Phone2Icon color="inherit" width={20} height={20} />
              {phone}
            </WhiteStyledButton>
          )}
        </StyledBtnsWrapper>
      ) : (
        <StyledBtnsWrapper>
          <RentRangePriceText>
            <RentRangePriceStrong>
              {apartment?.priceCurrency ?? "₪"}
              {typeof apartment?.price === "number"
                ? apartment.price.toLocaleString()
                : apartment?.price ?? "__"}
            </RentRangePriceStrong>
            {t("monthlyRent")}
          </RentRangePriceText>
          <Box sx={{ position: "relative", width: 180, flexGrow: 0, flexShrink: 0 }}>
            {mobileCtaOpen && (
              <HiddenButtons>
                {phone && (
                  <StyledPhoneLink href={`tel:${phone}`}>
                    <MobileOptionButton
                      variant="outlined"
                      component="span"
                      onClick={() => {
                        setMobileCtaOpen(false)
                        contactButtonClick(
                          "יצירת קשר לקבלת מחיר מנציג/ה",
                          "שיחה עם מוקד מכירות",
                          "כעמוד מידע על דירה",
                        )
                      }}
                    >
                      {t("callNow")}
                    </MobileOptionButton>
                  </StyledPhoneLink>
                )}
                {!isTheApartmentTaken && (
                  <MobileOptionButton
                    variant="outlined"
                    onClick={() => {
                      setMobileCtaOpen(false)
                      handleFormOpen()
                    }}
                  >
                    {t("leaveDetails")}
                  </MobileOptionButton>
                )}
              </HiddenButtons>
            )}
            <IWantRentBtn
              variant="outlined"
              style={{ flex: "unset", padding: "8px 24px", width: 180, gap: 8 }}
              onClick={() => setMobileCtaOpen((prev) => !prev)}
            >
              {mobileCtaOpen && <CircleXIcon />}
              {mobileCtaOpen ? t("close") : t("contactUs")}
            </IWantRentBtn>
          </Box>
        </StyledBtnsWrapper>
      )}
      <DrawerContactForm
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        formProps={{
          formData,
          messages,
          formSubjects,
          onClose: handleFormClose,
          bmbyId: apartment.leadFormBMBYId,
          status: status,
          companyId: companyId,
          newStyle: true,
        }}
        paymentForm
        rentalHousingFormProps={
          rentalProjects
            ? {
                onClose: handleFormClose,
                messages,
                bmbyId: apartment.leadFormBMBYId,
                companyId,
                emailForLead: apartment.emailForLead,
                projects: rentalProjects,
                cmsProjectLeadIds,
                preSelectedProject,
                preSelectedRooms: apartment.numberOfRooms ? [apartment.numberOfRooms] : undefined,
              }
            : undefined
        }
      />
    </Container>
  )
}

export default DrawerInner
